import { useState } from "react"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { updateProfile } from "firebase/auth"
import { auth, storage } from "@/config/firebase"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB

interface UseUpdateProfilePictureResult {
  uploadProfilePicture: (file: File) => Promise<string | null>
  isUploading: boolean
  error: string | null
}

const useUpdateProfilePicture = (): UseUpdateProfilePictureResult => {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const uploadProfilePicture = async (file: File): Promise<string | null> => {
    const user = auth.currentUser
    if (!user) {
      setError("You must be signed in to update your profile picture.")
      return null
    }

    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed.")
      return null
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("Image must be smaller than 5 MB.")
      return null
    }

    setError(null)
    setIsUploading(true)

    try {
      const ext = file.name.split(".").pop() || "jpg"
      const storageRef = ref(storage, `${user.uid}/avatar/profile_${Date.now()}.${ext}`)
      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)
      await updateProfile(user, { photoURL: downloadURL })
      return downloadURL
    } catch (err) {
      console.error("Profile picture upload failed:", err)
      setError("Failed to update profile picture. Please try again.")
      return null
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadProfilePicture, isUploading, error }
}

export { useUpdateProfilePicture }
